import { EditorState, StateField, type Extension } from '@codemirror/state'
import { hoverTooltip, showTooltip, type EditorView, type Tooltip } from '@codemirror/view'
import {
  autocompletion, acceptCompletion, completeAnyWord, completionKeymap, completionStatus, type CompletionSource,
  type Completion as CMCompletion, type CompletionContext as CMContext, type CompletionResult as CMResult,
} from '@codemirror/autocomplete'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { javascriptLanguage, scopeCompletionSource } from '@codemirror/lang-javascript'
import { intelForLanguage } from '../code-intel/sources.js'
import { docBlocks, inlineRuns } from '../code-intel/doc-format.js'
import type { AppCodeContext, Completion, SignatureSource } from '../code-intel/types.js'

/**
 * The code-intel sources (code-intel/*) wired into CodeMirror's own
 * autocompletion and hover tooltips, beside the language's own completions —
 * the sources already speak CodeMirror's shapes, so this is only the context
 * and DOM glue.
 */

export interface IntelEnv { fileName: string; app: AppCodeContext | null }

/**
 * Typed text a result stays valid for, without asking again. A path (an op or script name: `_db/x.sql`) keeps
 * `/`, `.`, `-`; a name stops at them — `papan` then `.` must ask again for `papan`'s members, not filter the
 * names list down to nothing.
 */
const VALID_FOR_PATH = /^[\w$/.\-]*$/
const VALID_FOR_NAME = /^[\w$]*$/
const HOVER_DELAY_MS = 500

/** Doc text → DOM: flowing paragraphs, `inline code`, indented examples kept as code. */
export function docDom(text: string, title?: string): HTMLElement {
  const root = document.createElement('div')
  root.className = 'cm-papan-doc'
  if (title) {
    const pre = document.createElement('pre')
    pre.className = 'cm-papan-doc-title'
    pre.textContent = title
    root.appendChild(pre)
  }
  for (const b of docBlocks(text)) {
    if (b.kind === 'code') {
      const pre = document.createElement('pre')
      pre.className = 'cm-papan-doc-code'
      pre.textContent = b.text
      root.appendChild(pre)
      continue
    }
    const p = document.createElement('p')
    for (const r of inlineRuns(b.text)) {
      if (r.code) {
        const code = document.createElement('code')
        code.textContent = r.text
        p.appendChild(code)
      } else {
        p.appendChild(document.createTextNode(r.text))
      }
    }
    root.appendChild(p)
  }
  return root
}

/** A completion's info: its signature (when it opens with one) as the title, then the docs. */
function infoDom(c: Completion): HTMLElement {
  const info = c.info ?? ''
  const cut = info.indexOf('\n\n')
  const head = cut < 0 ? info : info.slice(0, cut)
  if ((c.type === 'method' || c.type === 'property') && head.startsWith(c.label)) {
    return docDom(cut < 0 ? '' : info.slice(cut + 2), head)
  }
  return docDom(info)
}

/** The list shows a short detail; the full signature is in the info panel beside it. */
const DETAIL_MAX = 36
const shortDetail = (d: string | undefined) => (d && d.length > DETAIL_MAX ? d.slice(0, DETAIL_MAX - 1) + '…' : d)

function toCM(c: Completion): CMCompletion {
  return {
    label: c.label,
    type: c.type,
    detail: shortDetail(c.detail),
    apply: c.apply,
    boost: c.boost,
    info: c.info ? () => infoDom(c) : undefined,
  }
}

/**
 * The signature of the SDK / helper call the caret is inside, shown above its
 * line while typing the arguments. Quiet by design: only with an empty
 * selection, never while the completion list is open (it owns that space), and
 * the first paragraph of the docs only — the rest is a hover away.
 */
function signatureHint(signature: SignatureSource, env: () => IntelEnv): Extension {
  type Shown = { tip: Tooltip; sig: string; line: number }
  const compute = (state: EditorState, prev: Shown | null): Shown | null => {
    const sel = state.selection.main
    // Gives way only to a SHOWN list — not to a pending request, or it would flicker on every keystroke.
    if (!sel.empty || completionStatus(state) === 'active') return null
    const { fileName, app } = env()
    const help = signature({ doc: state.doc.toString(), pos: sel.head, explicit: false, fileName, app })
    if (!help) return null
    const line = state.doc.lineAt(sel.head).number
    // Same call on the same line: keep the shown tooltip (no flicker while typing arguments).
    if (prev && prev.sig === help.signature && prev.line === line) return prev
    const tip: Tooltip = {
      pos: sel.head,
      above: true,
      arrow: false,
      create: () => {
        const dom = docDom(help.doc.split(/\n\s*\n/)[0] ?? '', help.signature.split('\n')[0])
        dom.classList.add('cm-papan-signature')
        return { dom }
      },
    }
    return { tip, sig: help.signature, line }
  }
  const field = StateField.define<Shown | null>({
    create: (state) => compute(state, null),
    update(shown, tr) {
      if (!tr.docChanged && !tr.selection && completionStatus(tr.state) === completionStatus(tr.startState)) return shown
      return compute(tr.state, shown)
    },
  })
  return [field, showTooltip.compute([field], (state) => state.field(field)?.tip ?? null)]
}

/** Languages whose CodeMirror package brings its own completions (keywords, local names, tags, properties). */
const JS_LANGUAGES = new Set(['javascript', 'typescript', 'html'])

/**
 * The browser's own names (`document`, `window`, `fetch`, … and their members after a dot) for JS, as Monaco's
 * DOM library gives them. Read off a blank frame's window, made on the first completion — never this page's,
 * whose own globals (the app's, extensions') would be listed as if a page could use them.
 */
let browserScope: CompletionSource | null | undefined
const browserGlobals: CompletionSource = (cx) => {
  if (browserScope === undefined) {
    browserScope = null
    try {
      const frame = document.createElement('iframe')
      frame.setAttribute('aria-hidden', 'true')
      frame.tabIndex = -1
      frame.style.display = 'none'
      document.body.appendChild(frame)
      if (frame.contentWindow) browserScope = scopeCompletionSource(frame.contentWindow)
    } catch { /* no frame: no browser names */ }
  }
  return browserScope ? browserScope(cx) : null
}

const OWN_COMPLETIONS = new Set(['javascript', 'typescript', 'html', 'css', 'scss', 'less', 'sql', 'python'])

/**
 * Completion and hover for `language`, as Monaco does it: the language's own
 * completions (keywords and local names in JS / Python, tags and attributes in
 * HTML, properties in CSS, SQL keywords) beside the code-intel sources (the
 * PDApp SDK, op paths, schema tables — code-intel/*), and the words of the
 * document for a language with none of its own. Read-only views (`complete:
 * false`) get hover only.
 */
export function intelExtensions(language: string | undefined, env: () => IntelEnv, opts: { complete: boolean }): Extension {
  const intel = intelForLanguage(language)
  const exts: Extension[] = []
  if (opts.complete) {
    // Sources go in as language data, never `override`: an override would replace the language's own.
    const sources: CompletionSource[] = []
    if (intel) {
      sources.push(async (cm: CMContext): Promise<CMResult | null> => {
        const { fileName, app } = env()
        const r = await intel.complete({ doc: cm.state.doc.toString(), pos: cm.pos, explicit: cm.explicit, fileName, app })
        if (!r || r.options.length === 0) return null
        const path = r.options.some((o) => /[/.\-]/.test(o.label))
        return { from: r.from, to: r.to, options: r.options.map(toCM), validFor: path ? VALID_FOR_PATH : VALID_FOR_NAME }
      })
    }
    if (!language || !OWN_COMPLETIONS.has(language)) sources.push(completeAnyWord)
    // In JS only — inside an HTML file, only its scripts (the JS language's own data).
    if (language && JS_LANGUAGES.has(language)) exts.push(javascriptLanguage.data.of({ autocomplete: browserGlobals }))
    exts.push(
      autocompletion({ icons: true, maxRenderedOptions: 100 }),
      EditorState.languageData.of(() => sources.map((autocomplete) => ({ autocomplete }))),
      // Tab accepts like Enter while the list is open; otherwise Tab indents.
      Prec.high(keymap.of([{ key: 'Tab', run: acceptCompletion }, ...completionKeymap])),
    )
    if (intel?.signature) exts.push(signatureHint(intel.signature, env))
  }
  if (intel?.hover) {
    const hover = intel.hover
    exts.push(hoverTooltip(async (view: EditorView, pos: number, side: -1 | 1) => {
      const { fileName, app } = env()
      const at = side < 0 ? pos - 1 : pos
      const info = await hover({ doc: view.state.doc.toString(), pos: at, explicit: false, fileName, app })
      if (!info) return null
      return { pos: info.from, end: info.to, above: false, create: () => ({ dom: docDom(info.doc, info.title) }) }
    }, { hoverTime: HOVER_DELAY_MS }))
  }
  return exts
}
